import { PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react'
import { OcrRegion } from '@api/ocr/api'
import { pointerPoint, validRegion } from './region'

export type LineEdge = 'top' | 'right' | 'bottom' | 'left'
interface Options {
    disabled: boolean
    resetKey: string
    onSelect: (key: string) => void
    onCommit: (key: string, region: OcrRegion) => void
}
interface ResizeGesture {
    key: string
    edge: LineEdge
    pointerId: number
    target: HTMLDivElement
    start: {x: number; y: number}
    original: OcrRegion
    region: OcrRegion
}
const minimum = .002
const clamp = (value: number, low: number, high: number) => Math.max(low, Math.min(high, value))
const sameRegion = (a: OcrRegion, b: OcrRegion) => (['x', 'y', 'width', 'height'] as const).every(key => Math.abs(a[key] - b[key]) < 1e-10)

/** Resize previews stay local until release, so autosave never stores a half-finished gesture. */
export function useLineEditing(options: Options) {
    const latest = useRef(options); latest.current = options
    const documentRef = useRef<HTMLDivElement>(null)
    const active = useRef<ResizeGesture | null>(null)
    const [preview, setPreview] = useState<{key: string; region: OcrRegion; edge: LineEdge} | null>(null)

    const focusDocument = () => documentRef.current?.focus({preventScroll: true})
    const finish = (commit: boolean) => {
        const gesture = active.current
        if (!gesture) return
        active.current = null; setPreview(null)
        try {gesture.target.releasePointerCapture?.(gesture.pointerId)} catch { /* Capture may already be lost. */ }
        if (commit && !latest.current.disabled && !sameRegion(gesture.original, gesture.region)) latest.current.onCommit(gesture.key, gesture.region)
    }
    useEffect(() => {
        const blur = () => finish(false)
        const keyDown = (event: KeyboardEvent) => {if (event.key === 'Escape') finish(false)}
        window.addEventListener('blur', blur); window.addEventListener('keydown', keyDown)
        return () => {window.removeEventListener('blur', blur); window.removeEventListener('keydown', keyDown); finish(false)}
        // All callbacks read current options through a ref.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    useEffect(() => {finish(false)}, [options.resetKey, options.disabled]) // eslint-disable-line react-hooks/exhaustive-deps

    const update = (event: ReactPointerEvent<HTMLDivElement>) => {
        const gesture = active.current, document = documentRef.current
        if (!gesture || gesture.pointerId !== event.pointerId || !document) return
        const point = pointerPoint(event.clientX, event.clientY, document.getBoundingClientRect())
        const dx = point.x - gesture.start.x, dy = point.y - gesture.start.y, original = gesture.original
        const right = original.x + original.width, bottom = original.y + original.height
        const region = {...original}
        if (gesture.edge === 'left') {region.x = clamp(original.x + dx, 0, right - Math.min(minimum, original.width)); region.width = right - region.x}
        if (gesture.edge === 'right') region.width = clamp(right + dx, original.x + Math.min(minimum, original.width), 1) - original.x
        if (gesture.edge === 'top') {region.y = clamp(original.y + dy, 0, bottom - Math.min(minimum, original.height)); region.height = bottom - region.y}
        if (gesture.edge === 'bottom') region.height = clamp(bottom + dy, original.y + Math.min(minimum, original.height), 1) - original.y
        gesture.region = region
        setPreview({key: gesture.key, edge: gesture.edge, region})
    }
    const handlers = (key: string, region: OcrRegion, edge: LineEdge) => ({
        onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => {
            // Ctrl retains priority for drawing a new line, even when the pointer starts on an old edge.
            if (event.ctrlKey) return
            event.stopPropagation()
            if (event.button !== 0 || latest.current.disabled || active.current || !validRegion(region)) return
            const document = documentRef.current
            if (!document) return
            event.preventDefault(); latest.current.onSelect(key); focusDocument()
            active.current = {key, edge, pointerId: event.pointerId, target: event.currentTarget, start: pointerPoint(event.clientX, event.clientY, document.getBoundingClientRect()), original: {...region}, region: {...region}}
            setPreview({key, region, edge})
            event.currentTarget.setPointerCapture?.(event.pointerId)
        },
        onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => {if (active.current?.pointerId === event.pointerId) {event.stopPropagation(); event.preventDefault(); update(event)}},
        onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => {if (active.current?.pointerId === event.pointerId) {event.stopPropagation(); event.preventDefault(); update(event); finish(true)}},
        onPointerCancel: (event: ReactPointerEvent<HTMLDivElement>) => {if (active.current?.pointerId === event.pointerId) {event.stopPropagation(); finish(false)}},
        onLostPointerCapture: (event: ReactPointerEvent<HTMLDivElement>) => {if (active.current?.pointerId === event.pointerId) {event.stopPropagation(); finish(false)}}
    })
    return {documentRef, focusDocument, handlers, preview, isResizing: () => active.current !== null}
}

export function moveLineRegion(region: OcrRegion, key: string, stepX: number, stepY: number): OcrRegion {
    return {...region, x: clamp(region.x + (key === 'ArrowRight' ? stepX : key === 'ArrowLeft' ? -stepX : 0), 0, Math.max(0, 1 - region.width)), y: clamp(region.y + (key === 'ArrowDown' ? stepY : key === 'ArrowUp' ? -stepY : 0), 0, Math.max(0, 1 - region.height))}
}
